import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, Save, Trash2 } from "lucide-react";
import { ApiQuota, useUpdateQuota, useCleanupLogs } from "@/hooks/useApiUsage";
import { toast } from "sonner";

interface QuotaSettingsProps {
  quotas: ApiQuota[];
  isLoading?: boolean;
}

const SERVICE_LABELS: Record<string, string> = {
  resend: "Sähköposti (Resend)",
  "46elks": "SMS (46elks)",
};

export function QuotaSettings({ quotas, isLoading }: QuotaSettingsProps) {
  const [editedQuotas, setEditedQuotas] = useState<Record<string, Partial<ApiQuota>>>({});
  const updateQuota = useUpdateQuota();
  const cleanupLogs = useCleanupLogs();

  const handleChange = (quotaId: string, field: keyof ApiQuota, value: unknown) => {
    setEditedQuotas((prev) => ({
      ...prev,
      [quotaId]: {
        ...prev[quotaId],
        [field]: value,
      },
    }));
  };

  const handleSave = async (quota: ApiQuota) => {
    const changes = editedQuotas[quota.id];
    if (!changes || Object.keys(changes).length === 0) return;

    try {
      await updateQuota.mutateAsync({
        id: quota.id,
        ...changes,
      });
      setEditedQuotas((prev) => {
        const newState = { ...prev };
        delete newState[quota.id];
        return newState;
      });
      toast.success("Kiintiöasetukset tallennettu");
    } catch (error) {
      toast.error("Virhe tallennuksessa");
    }
  };

  const handleCleanup = async () => {
    try {
      const deleted = await cleanupLogs.mutateAsync();
      toast.success(`Poistettu ${deleted} vanhaa lokimerkintää`);
    } catch (error) {
      toast.error("Virhe lokien siivouksessa");
    }
  };

  const getValue = <K extends keyof ApiQuota>(quota: ApiQuota, field: K): ApiQuota[K] => {
    const edited = editedQuotas[quota.id]?.[field];
    return edited !== undefined ? (edited as ApiQuota[K]) : quota[field];
  };

  const hasChanges = (quotaId: string) => {
    const changes = editedQuotas[quotaId];
    return changes && Object.keys(changes).length > 0;
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Kiintiöasetukset</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            {[1, 2].map((i) => (
              <div key={i} className="h-32 bg-muted rounded" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Kiintiöasetukset</CardTitle>
        <CardDescription>Hallitse API-palveluiden käyttörajoja</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {quotas.map((quota) => (
          <div key={quota.id} className="border rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium">
                {SERVICE_LABELS[quota.service] || quota.service}
              </h3>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    id={`enabled-${quota.id}`}
                    checked={getValue(quota, "is_enabled")}
                    onCheckedChange={(checked) =>
                      handleChange(quota.id, "is_enabled", checked)
                    }
                  />
                  <Label htmlFor={`enabled-${quota.id}`}>Käytössä</Label>
                </div>
                {hasChanges(quota.id) && (
                  <Button
                    size="sm"
                    onClick={() => handleSave(quota)}
                    disabled={updateQuota.isPending}
                  >
                    {updateQuota.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    <span className="ml-1">Tallenna</span>
                  </Button>
                )}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <Label htmlFor={`hourly-${quota.id}`}>Tuntikiintiö</Label>
                <Input
                  id={`hourly-${quota.id}`}
                  type="number"
                  value={getValue(quota, "hourly_limit") ?? ""}
                  onChange={(e) =>
                    handleChange(
                      quota.id,
                      "hourly_limit",
                      e.target.value ? parseInt(e.target.value) : null
                    )
                  }
                  placeholder="Ei rajaa"
                />
              </div>
              <div>
                <Label htmlFor={`daily-${quota.id}`}>Päiväkiintiö</Label>
                <Input
                  id={`daily-${quota.id}`}
                  type="number"
                  value={getValue(quota, "daily_limit") ?? ""}
                  onChange={(e) =>
                    handleChange(
                      quota.id,
                      "daily_limit",
                      e.target.value ? parseInt(e.target.value) : null
                    )
                  }
                  placeholder="Ei rajaa"
                />
              </div>
              <div>
                <Label htmlFor={`monthly-${quota.id}`}>Kuukausikiintiö</Label>
                <Input
                  id={`monthly-${quota.id}`}
                  type="number"
                  value={getValue(quota, "monthly_limit") ?? ""}
                  onChange={(e) =>
                    handleChange(
                      quota.id,
                      "monthly_limit",
                      e.target.value ? parseInt(e.target.value) : null
                    )
                  }
                  placeholder="Ei rajaa"
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <Label htmlFor={`cost-${quota.id}`}>Yksikkökustannus (€)</Label>
                <Input
                  id={`cost-${quota.id}`}
                  type="number"
                  step="0.01"
                  value={getValue(quota, "cost_per_unit")}
                  onChange={(e) =>
                    handleChange(quota.id, "cost_per_unit", parseFloat(e.target.value) || 0)
                  }
                />
              </div>
              <div>
                <Label htmlFor={`threshold-${quota.id}`}>Hälytysraja (%)</Label>
                <Input
                  id={`threshold-${quota.id}`}
                  type="number"
                  min="0"
                  max="100"
                  value={Math.round(getValue(quota, "warning_threshold") * 100)}
                  onChange={(e) =>
                    handleChange(
                      quota.id,
                      "warning_threshold",
                      (parseInt(e.target.value) || 80) / 100
                    )
                  }
                />
              </div>
              <div>
                <Label htmlFor={`retention-${quota.id}`}>Lokien säilytys (pv)</Label>
                <Input
                  id={`retention-${quota.id}`}
                  type="number"
                  min="1"
                  value={getValue(quota, "retention_days")}
                  onChange={(e) =>
                    handleChange(quota.id, "retention_days", parseInt(e.target.value) || 90)
                  }
                />
              </div>
            </div>
          </div>
        ))}

        {/* Manual cleanup button */}
        <div className="flex justify-end pt-4 border-t">
          <Button
            variant="outline"
            onClick={handleCleanup}
            disabled={cleanupLogs.isPending}
          >
            {cleanupLogs.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Trash2 className="h-4 w-4 mr-2" />
            )}
            Siivoa vanhat lokit
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
